/**
 * Keeps Afterpay placements in sync with amounts that change client-side.
 *
 * Most amount changes need nothing here: the product price block and the cart drawer are both
 * re-rendered from the server (variant-picker.js swaps [data-dynamic-variant-content], and
 * cart-drawer.js replaces the drawer's innerHTML), so the placement comes back with a fresh
 * amount already applied.
 *
 * The exception is a running total that only exists in the browser — the bulk-order
 * configurator. For that, listen for 'on:afterpay:amount-change' and rebuild the placement.
 */
(() => {
  /**
   * Updates a placement's amount, re-applying the configured order-value limits.
   *
   * Mutating data-amount in place is not enough: the web component reads its attributes when it
   * upgrades, so an already-rendered placement would keep showing the old instalment figure.
   * Swapping the node forces a fresh upgrade.
   *
   * @param {string} targetId - Value of data-afterpay-placement to update.
   * @param {number} amountCents - New amount, in cents.
   */
  function updateAmount(targetId, amountCents) {
    const wrapper = document.querySelector(
      `[data-afterpay-placement="${CSS.escape(targetId)}"]`
    );
    if (!wrapper) return;

    const placement = wrapper.querySelector('square-placement');
    if (!placement) return;

    const amountMajor = (Number(amountCents) || 0) / 100;

    // An empty selection has nothing to advertise instalments for.
    if (amountMajor <= 0) {
      wrapper.hidden = true;
      return;
    }

    // Server-side gating is skipped for dynamic placements, so apply the limits here.
    const min = Number(wrapper.dataset.afterpayMin) || 0;
    const max = Number(wrapper.dataset.afterpayMax) || 0;
    const eligible = (min <= 0 || amountMajor >= min) && (max <= 0 || amountMajor <= max);

    // Match the 2-decimal format the Liquid snippet renders, so the value the widget parses
    // looks the same whether it came from the server or from here.
    const amount = amountMajor.toFixed(2);

    wrapper.hidden = false;

    if (placement.dataset.amount === amount
      && placement.dataset.isEligible === String(eligible)) {
      return;
    }

    const replacement = placement.cloneNode(true);
    replacement.dataset.amount = amount;
    replacement.dataset.isEligible = String(eligible);
    placement.replaceWith(replacement);
  }

  document.addEventListener('on:afterpay:amount-change', (evt) => {
    const { targetId, amountCents } = evt.detail || {};
    if (!targetId) return;
    updateAmount(targetId, amountCents);
  });
})();
